import React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Customized,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ChartAxisTick, chartDateTicks } from '../chartAxis';
import { scoreBandColor, scoreBandGradientStops } from '../scoreBandColor';
import { wellnessYDomain, wellnessYTicks } from '../wellnessYDomain';
import { AiMomentumDashboardResponse, WeeklyDirectionIndexDto } from '../types';
import { formatChartDate, formatWeekRange } from '../utils/formatDates';
import { chartTooltipStyle } from './chartTooltipStyle';

function firstYScale(yAxisMap?: Record<string, { scale?: (value: number) => number }>): ((value: number) => number) | undefined {
  if (!yAxisMap) {
    return undefined;
  }

  const axis = Object.keys(yAxisMap).map((key) => yAxisMap[key])[0];
  return axis && typeof axis.scale === 'function' ? axis.scale : undefined;
}

function WellnessStrokeGradient(props: object): React.ReactElement {
  const yAxisMap = (props as { yAxisMap?: Record<string, { scale?: (value: number) => number }> }).yAxisMap;
  const scale = firstYScale(yAxisMap);
  const y0 = scale ? scale(0) : undefined;
  const y100 = scale ? scale(100) : undefined;
  const mapped = typeof y0 === 'number' && typeof y100 === 'number' && y0 !== y100;

  return (
    <defs>
      <linearGradient
        id="ppWellnessStroke"
        gradientUnits={mapped ? 'userSpaceOnUse' : 'objectBoundingBox'}
        x1={0}
        y1={mapped ? y0 : 1}
        x2={0}
        y2={mapped ? y100 : 0}
      >
        {scoreBandGradientStops().map((stop, index) => (
          <stop key={`${stop.offset}-${stop.color}-${index}`} offset={`${stop.offset}%`} stopColor={stop.color} />
        ))}
      </linearGradient>
    </defs>
  );
}

interface WellnessTrendChartProps {
  points: { date: string; wellness?: number | null }[];
}

export function WellnessTrendChart({ points }: WellnessTrendChartProps): React.ReactElement {
  const data = points
    .filter((point) => point.wellness != null)
    .map((point) => ({
      date: point.date,
      wellness: point.wellness as number,
    }));
  const dateTicks = chartDateTicks(data.map((point) => point.date));
  const lastIndex = data.length - 1;
  const yDomain = wellnessYDomain(data.map((point) => point.wellness));
  const yTicks = wellnessYTicks(yDomain);

  return (
    <div className="mt-2">
      <div
        className="h-[min(18rem,55vw)] w-full min-h-[220px] sm:h-72"
        role="img"
        aria-label="Organisation Health Score during last 4 weeks"
      >
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 12 }}>
            <Customized component={WellnessStrokeGradient} />
            <CartesianGrid vertical={false} stroke="rgba(0,75,77,0.12)" strokeDasharray="4 6" />
            <XAxis
              dataKey="date"
              ticks={dateTicks}
              tickFormatter={(value: string) => formatChartDate(value)}
              tick={ChartAxisTick}
              tickLine={false}
              axisLine={false}
              interval={0}
            />
            <YAxis
              domain={yDomain}
              ticks={yTicks}
              allowDecimals={false}
              tick={ChartAxisTick}
              tickLine={false}
              axisLine={false}
              width={40}
            />
            <Tooltip
              contentStyle={chartTooltipStyle}
              labelFormatter={(value: string) => formatChartDate(value)}
            />
            <Line
              type="monotone"
              dataKey="wellness"
              stroke="url(#ppWellnessStroke)"
              strokeWidth={3}
              isAnimationActive={false}
              dot={(dotProps) => {
                const { cx, cy, index, key } = dotProps as {
                  cx?: number;
                  cy?: number;
                  index?: number;
                  key?: string | number;
                };
                if (cx == null || cy == null || index == null) {
                  return <g key={key} />;
                }

                if (index !== 0 && index !== lastIndex) {
                  return <g key={key} />;
                }

                if (index === lastIndex) {
                  return (
                    <g key={key}>
                      <circle cx={cx} cy={cy} r={8} fill="none" stroke="#004b4d" strokeWidth={1.5} />
                      <circle cx={cx} cy={cy} r={4} fill={scoreBandColor(data[index].wellness)} />
                    </g>
                  );
                }

                return <circle key={key} cx={cx} cy={cy} r={4} fill={scoreBandColor(data[index].wellness)} />;
              }}
              activeDot={{ r: 5, fill: '#004b4d' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

interface WeeklyDirectionIndexChartProps {
  points: WeeklyDirectionIndexDto[];
}

export function WeeklyDirectionIndexChart({ points }: WeeklyDirectionIndexChartProps): React.ReactElement {
  const data = points.map((point) => ({
    ...point,
    label: formatWeekRange(point.week_ending),
  }));

  return (
    <div
      className="mt-2 h-[min(18rem,55vw)] w-full min-h-[220px] sm:h-72"
      role="img"
      aria-label="Weekly wellness index"
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
          <CartesianGrid vertical={false} stroke="rgba(0,75,77,0.12)" strokeDasharray="4 6" />
          <XAxis
            dataKey="label"
            tick={ChartAxisTick}
            tickLine={false}
            axisLine={false}
            interval={0}
          />
          <YAxis
            domain={[-1, 1]}
            ticks={[-1, -0.5, 0, 0.5, 1]}
            tick={ChartAxisTick}
            tickLine={false}
            axisLine={false}
            width={40}
          />
          <ReferenceLine y={0} stroke="rgba(0,75,77,0.35)" />
          <Tooltip
            contentStyle={chartTooltipStyle}
            labelFormatter={(_value, payload) => {
              const ending = payload?.[0]?.payload?.week_ending as string | undefined;
              return ending ? formatWeekRange(ending) : '';
            }}
            formatter={(value: number) => [Number(value).toFixed(2), 'Change']}
          />
          <Bar dataKey="index" maxBarSize={48} isAnimationActive={false}>
            {data.map((point) => (
              <Cell
                key={point.week_ending}
                fill={point.index > 0 ? '#8bc34a' : point.index < 0 ? '#e57373' : '#bde3ec'}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
